import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { DehydratedState } from 'react-query';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Auth0Provider } from '@auth0/auth0-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import type { BugsnagErrorBoundary } from '@bugsnag/plugin-react';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import type { EmotionCache } from '@emotion/react';
import { CacheProvider } from '@emotion/react';
import { GoogleApiProvider, type PlaceLocation } from '@goodfynd/context';
import { SnackbarProvider } from '@goodfynd/context';
import { createEmotionCache, ThemeProvider } from '@goodfynd/theme';
import CssBaseline from '@mui/material/CssBaseline';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Loading } from '../components';
import Layout from '../components/layout/Layout';
import { StyledFlexCenter } from '../components/styles';
import config from '../config';
import env from '../config/env';
import routes from '../config/routes';
import strings from '../config/strings';
import { AppProvider } from '../context/app-context';
import { LocationProvider } from '../context/location-context';
import type { RootStore } from '../stores';
import { DefaultState, useRootStore } from '../stores';
import { RootStoreProvider } from '../stores/root-store/root-store-context';

import '@goodfynd/react-web/theme/fonts/fonts.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import '@goodfynd/react-web/design-system/css-vars/themes.css';
import './global.css';

const bugsnagApiKey = config.env.BUGSNAG_API_KEY;
const bugsnagReleaseStage = config.env.NAME.toLowerCase();

if (bugsnagApiKey) {
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    enabledReleaseStages: [bugsnagReleaseStage],
    plugins: [new BugsnagPluginReact()],
    releaseStage: bugsnagReleaseStage,
  });

  if (['production', 'staging'].includes(bugsnagReleaseStage)) {
    BugsnagPerformance.start({ apiKey: bugsnagApiKey });
  }
}

import { DialogProvider } from '@goodfynd/ui';
import { local } from '@utils/storage-util';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';

import HeadScripts from './_head-scripts';

if (config.app.mode.isProduction) {
  console.debug = () => console.info();
  console.error = () => console.info();
  console.log = () => console.info();
  console.warn = () => console.info();
}

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(
  React
) as BugsnagErrorBoundary;

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout = AppProps<{ dehydratedState: DehydratedState }> & {
  emotionCache?: EmotionCache;
  Component: NextPageWithLayout;
};

function MyApp({
  Component,
  pageProps: { dehydratedState, ...pageProps },
}: AppPropsWithLayout) {
  const [emotionCache, setEmotionCache] = useState<EmotionCache>();
  const createRootStore = useRootStore();
  const {
    query: { address = '', latitude, longitude },
    ...router
  } = useRouter();

  const [enRoute, toggleRouting] = useState(false);

  useEffect(() => {
    // Client-side cache, shared for the whole session of the user in the browser.
    setEmotionCache(createEmotionCache());
  }, []);

  useEffect(() => {
    (async () => {
      setRootStore(await createRootStore()); // configure up mobx-state-tree
      // Now wait for state tree to be ready
    })();
  }, [createRootStore]);

  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      })
  );

  const [rootStore, setRootStore] = useState<RootStore>(DefaultState);

  const handleRouteChange = useCallback(() => {
    toggleRouting(false);

    if (
      JSON.parse(process.env.NEXT_PUBLIC_MAINTENANCE_MODE || 'false') &&
      !router.pathname.endsWith(routes.maintenance)
    ) {
      window.location.href = routes.maintenance;
      return;
    }
  }, [router.pathname]);

  useEffect(() => {
    const handleRouteChangeError = () => toggleRouting(false);
    const handleRouteChangeStart = () => toggleRouting(true);

    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('routeChangeError', handleRouteChangeError);
    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('routeChangeError', handleRouteChangeError);
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [handleRouteChange, router.events]);

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    router.replace(appState?.returnTo || '/');
  };

  const defaultLocation = useMemo(() => {
    if (typeof latitude === 'string' && typeof longitude === 'string') {
      return {
        position: {
          address: address as string,
          coordinates: `${latitude},${longitude}`,
          latitude: Number(latitude),
          longitude: Number(longitude),
        },
      };
    }

    const userLocation =
      global?.window?.localStorage &&
      local.get<Partial<PlaceLocation>>(config.storageKeys.userLocation);
    if (userLocation) {
      return {
        position: userLocation,
      };
    }

    return undefined;
  }, [address, latitude, longitude]);

  // Wait for state to be ready
  if (emotionCache && rootStore) {
    const getLayout =
      Component.getLayout ?? ((page) => <Layout>{page}</Layout>);
    return (
      <>
        <ErrorBoundary>
          <ThemeProvider>
            <Auth0Provider
              clientId={process.env.NEXT_PUBLIC_CLIENT_ID || ''}
              domain={process.env.NEXT_PUBLIC_DOMAIN || ''}
              onRedirectCallback={onRedirectCallback}
              useRefreshTokens={true}
              useRefreshTokensFallback={true}
              authorizationParams={{
                scope: 'openid email offline_access',
                audience: process.env.NEXT_PUBLIC_AUDIENCE,
                redirect_uri:
                  (typeof window !== 'undefined' &&
                    window.location.origin + routes.account.callback) ||
                  '',
              }}
            >
              <RootStoreProvider value={rootStore}>
                <QueryClientProvider client={queryClient}>
                  <Hydrate state={dehydratedState}>
                    <CacheProvider value={emotionCache}>
                      <Head>
                        <title>{strings.app.name}</title>
                        <meta
                          content="initial-scale=1, width=device-width"
                          name="viewport"
                        />
                      </Head>
                      <GoogleApiProvider
                        apiKey={env.GOOGLE_API_KEY}
                        blocking={global.window?.location.host.startsWith(
                          'embed.'
                        )}
                      >
                        <LocationProvider defaultLocation={defaultLocation}>
                          <DialogProvider>
                            <SnackbarProvider>
                              <AppProvider>
                                {getLayout(
                                  <>
                                    <CssBaseline />

                                    {enRoute ? (
                                      <StyledFlexCenter>
                                        <Loading />
                                      </StyledFlexCenter>
                                    ) : (
                                      <Component {...pageProps} />
                                    )}
                                  </>
                                )}
                              </AppProvider>
                            </SnackbarProvider>
                          </DialogProvider>
                        </LocationProvider>
                      </GoogleApiProvider>
                    </CacheProvider>
                  </Hydrate>

                  {!config.app.mode.isProduction && <ReactQueryDevtools />}
                </QueryClientProvider>
              </RootStoreProvider>
            </Auth0Provider>
          </ThemeProvider>
        </ErrorBoundary>
        <HeadScripts />
      </>
    );
  }

  return null;
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// export async function getServerSideProps(context) {
//   return {
//     props: {
//       name: 'Lemaire S.',
//     }, // will be passed to the page component as props
//   };
// }

export default MyApp;
